// src/pages/outsourcing_management/requisitions/RequisitionGeneralInfoFields.tsx
//
// EE-207 Phase 3 (#9) - base layer of the create/edit Draft form: mandatory
// General Info (Type switch + Purpose + conditional SO/Part picker + Required
// By date), optional Supplier Info (company/contact/ref-link, read-only once
// a supplier is picked - the backend only persists supplierId, see
// CreateRequisitionIM), and Comments. Type/purpose/etc. are all
// code-keyed against useRequisitionOptions - never hardcode labels.
import { FunctionComponent, useEffect, useState } from "react";
import {
  Grid,
  Link,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { FormApi } from "@tanstack/react-form";
import { RequisitionOptionsType } from "types/RequisitionTypes";
import RequisitionSalesOrderPartPicker, {
  PickedPart,
} from "./RequisitionSalesOrderPartPicker";
import RequisitionQuotePartPicker from "./RequisitionQuotePartPicker";
import {
  REQ_TYPE_FINISH,
  PURPOSE_SALES_QUOTE,
  RequisitionFormHeaderValues,
  RequisitionHeaderErrors,
  inquiryTypeForRequisitionType,
} from "./requisitionForm";
import { SupplierAutocomplete } from "components/Dashboard/supplierOption";
import { usePartners } from "hooks/usePartners";

type PropsType = {
  form: FormApi<RequisitionFormHeaderValues, any>;
  /** Reports the picked parts upward so the item lines can start from them. */
  onPartsResolved?: (parts: PickedPart[]) => void;
  values: RequisitionFormHeaderValues;
  options?: RequisitionOptionsType;
  token: string;
  headerErrors: RequisitionHeaderErrors;
  showErrors: boolean;
  onTypeChange: (typeCode: string) => void;
  onPurposeChange: (purposeCode: string) => void;
};

const RequisitionGeneralInfoFields: FunctionComponent<PropsType> = ({
  form,
  values,
  options,
  token,
  headerErrors,
  showErrors,
  onTypeChange,
  onPurposeChange,
  onPartsResolved,
}) => {
  const purpose = options?.purposes.find((p) => p.code === values.purposeCode);
  // Finishing is the one type where the part's finish is the subject of the
  // request, so the picker shows it - and refuses parts without one - only
  // there. Mirrors _require_finish in RequisitionController.py.
  const requireFinish = values.typeCode === REQ_TYPE_FINISH;
  // Which side this requisition belongs to. Latched rather than derived:
  // clearing the Quote in its own Autocomplete would otherwise flip this to
  // false and swap the picker out mid-edit. The latch does drop when every
  // reference is gone, which is what a purpose change leaves behind - a
  // Sales Order requisition that still showed the quote picker because a
  // quote had once been picked under Sales Quote had no way to an order.
  // The Sales Quote purpose forces the quote side regardless, below.
  //
  // The Outsource button that opened the form prefills one reference or the
  // other; a requisition raised from scratch has neither and gets the order
  // picker, which is the common case.
  // `.length > 0`, not `!!array`: an empty array is truthy and would latch
  // every requisition onto the quote side.
  const [isQuoteSide, setIsQuoteSide] = useState(
    () => !!values.quoteId || values.quoteItemIds.length > 0
  );
  useEffect(() => {
    if (values.quoteId || values.quoteItemIds.length > 0) setIsQuoteSide(true);
    else setIsQuoteSide(false);
  }, [values.quoteId, values.quoteItemIds]);

  // Saying the purpose is a Sales Quote settles the question outright, so a
  // requisition raised from scratch against a quote no longer has to be
  // started from the Quote row to get the right picker.
  //
  // Only this purpose forces it. Sales Order does not: requisitions raised
  // before Sales Quote existed carry that purpose with a quote reference
  // attached, and forcing them to the order picker would hide the reference
  // they actually hold.
  const showQuotePicker = values.purposeCode === PURPOSE_SALES_QUOTE || isQuoteSide;
  // The read-only company/contact/ref panel below needs the whole partner,
  // not just its id, so the list is still read here.
  const partners = usePartners() ?? [];
  const selectedPartner = partners.find((p) => p.id === values.supplierId) ?? null;

  return (
    <div>
      {/* General Info (mandatory) */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        General Info
      </Typography>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Type
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={values.typeCode || null}
            onChange={(_, value) => {
              if (value) onTypeChange(value);
            }}
          >
            {options?.types.map((type) => (
              <ToggleButton key={type.code} value={type.code}>
                {type.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {showErrors && headerErrors.typeCode && (
            <Typography variant="caption" color="error" display="block">
              {headerErrors.typeCode}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <form.Field name="purposeCode">
            {(field) => (
              <TextField
                select
                fullWidth
                size="small"
                label="Purpose"
                required
                value={field.state.value}
                onChange={(e) => onPurposeChange(e.target.value)}
                error={showErrors && !!headerErrors.purposeCode}
                helperText={showErrors ? headerErrors.purposeCode : undefined}
              >
                <MenuItem value="">Select...</MenuItem>
                {options?.purposes.map((p) => (
                  <MenuItem key={p.code} value={p.code}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </form.Field>
        </Grid>

        <Grid item xs={12} sm={6}>
          <form.Field name="requiredByDate">
            {(field) => (
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Expected Delivery Date"
                InputLabelProps={{ shrink: true }}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
              />
            )}
          </form.Field>
        </Grid>

        {/* A requisition is raised from an order or from a quote, never
            both, so exactly one picker is shown. Which one is decided by
            whichever reference the form already carries - the Outsource
            button that opened it prefills one of them - and defaults to the
            order side for a requisition raised from scratch. */}
        {purpose?.requiresSalesOrderAndPart &&
          (showQuotePicker ? (
            <Grid item xs={12}>
              <RequisitionQuotePartPicker
                token={token}
                quoteId={values.quoteId}
                quoteItemIds={values.quoteItemIds}
                quoteError={showErrors ? headerErrors.salesOrderId : undefined}
                quoteItemError={showErrors ? headerErrors.orderItemId : undefined}
                requireFinish={requireFinish}
                typeCode={values.typeCode}
                onPartsResolved={onPartsResolved}
                onChange={({ quoteId, quoteItemIds }) => {
                  form.setFieldValue("quoteId", quoteId);
                  form.setFieldValue("quoteItemIds", quoteItemIds);
                  // The two pairs are mutually exclusive server-side.
                  form.setFieldValue("salesOrderId", undefined);
                  form.setFieldValue("orderItemIds", []);
                }}
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <RequisitionSalesOrderPartPicker
                token={token}
                salesOrderId={values.salesOrderId}
                orderItemIds={values.orderItemIds}
                salesOrderError={showErrors ? headerErrors.salesOrderId : undefined}
                orderItemError={showErrors ? headerErrors.orderItemId : undefined}
                requireFinish={requireFinish}
                typeCode={values.typeCode}
                onPartsResolved={onPartsResolved}
                onChange={({ salesOrderId, orderItemIds }) => {
                  form.setFieldValue("salesOrderId", salesOrderId);
                  form.setFieldValue("orderItemIds", orderItemIds);
                  form.setFieldValue("quoteId", undefined);
                  form.setFieldValue("quoteItemIds", []);
                }}
              />
            </Grid>
          ))}
      </Grid>

      {/* Supplier Info (optional) */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
        Supplier Info (optional)
      </Typography>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={6}>
          {/* Who the requester suggests asking, not who the work is ordered
              from. It goes onto the Inquiry's shortlist at submit - the
              settled supplier is decided later, when one of them wins the
              order. See ensure_enquiry_for_requisition. */}
          <SupplierAutocomplete
            value={values.supplierId}
            onChange={(supplierId) => form.setFieldValue("supplierId", supplierId)}
            label="Suggested supplier"
            helperText="Added to the inquiry's shortlist - more can be added there"
            serviceType={inquiryTypeForRequisitionType(values.typeCode)}
          />
        </Grid>
        {selectedPartner && (
          <Grid item xs={12} sm={6}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="body2">
                <strong>Company:</strong> {selectedPartner.info?.company || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Contact:</strong>{" "}
                {`${selectedPartner.info?.firstName ?? ""} ${selectedPartner.info?.lastName ?? ""}`.trim() ||
                  "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Ref:</strong>{" "}
                {selectedPartner.info?.email ? (
                  <Link href={`mailto:${selectedPartner.info.email}`}>
                    {selectedPartner.info.email}
                  </Link>
                ) : (
                  selectedPartner.info?.phone || "-"
                )}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Comments */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
        Comments
      </Typography>
      <form.Field name="comments">
        {(field) => (
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder="Additional comments..."
            value={field.state.value}
            onChange={(e) => field.handleChange(e.target.value)}
          />
        )}
      </form.Field>
    </div>
  );
};

export default RequisitionGeneralInfoFields;
